import React, { useState, useRef, useEffect } from "react";
import { useBeamTrussStore } from "../../hooks/useBeamTrussManagement";
import { useModelStore } from "../../store/useModelStore";

// ADD: Module-level storage for beam input persistence (distributed loads only)
const beamInputStorage: { [beamId: string]: Partial<LoadValues> } = {};

interface DynamicDistributedLoadPanelProps {
  beamId: string;
  beamIds?: string[]; // Optional array of beam IDs for multi-beam application
  position: { x: number; y: number };
  onClose: () => void;
  inlineMode: boolean;
}

type LoadType = "distributed1" | "distributed2" | "concentrated" | "twisted";
type ReferenceNode = "N1" | "N2" | "Center";

interface LoadValues {
  // Distributed 1 (uniform) - simple values
  dx: number;
  dy: number;
  dz: number;

  // Distributed 2 (variable) - start/end values
  dxStart: number;
  dxEnd: number;
  dyStart: number;
  dyEnd: number;
  dzStart: number;
  dzEnd: number;

  // Concentrated load
  fx: number;
  fy: number;
  fz: number;
  referenceNode: ReferenceNode;
  percentageFromNode: number;

  // Twisted load (torsional moment)
  torque: number;
  torqueDirection: "clockwise" | "anticlockwise";
}

export const DynamicDistributedLoadPanel: React.FC<
  DynamicDistributedLoadPanelProps
> = ({ beamId, beamIds, position, onClose, inlineMode }) => {
  const { addBeamTruss, removeBeamTruss, getTrussesForBeam } =
    useBeamTrussStore();

  // ADD: Access to model store for beam and node data
  const modelStore = useModelStore();

  // ADD: Function to determine which distributed section this beam corresponds to
  const getBeamIndex = (beamId: string): number => {
    if (!modelStore?.selectedBeamIds) return 0;
    const index = modelStore.selectedBeamIds.indexOf(beamId);
    return index >= 0 ? index : 0;
  };

  // ADD: Function to get node tags for a beam
  const getBeamNodeTags = (beamId: string): { n1: string; n2: string } => {
    if (!modelStore?.beams || !modelStore?.nodes) {
      return { n1: "N?", n2: "N?" };
    }

    const beam = modelStore.beams.find((b) => b.id === beamId);
    if (!beam) {
      return { n1: "N?", n2: "N?" };
    }

    const startNode = modelStore.nodes.find((n) => n.id === beam.startNodeId);
    const endNode = modelStore.nodes.find((n) => n.id === beam.endNodeId);

    return {
      n1: startNode?.id || "N?",
      n2: endNode?.id || "N?",
    };
  };

  // ADD: Get current beam's node tags and update when beam data changes
  const beamNodeTags = getBeamNodeTags(beamId);

  // ADD: New state variable for beam input tracking (distributed loads only) - using module storage for persistence
  const [beamInputData, setBeamInputData] = useState<{
    [beamId: string]: Partial<LoadValues>;
  }>(() => beamInputStorage);

  // Default to Distributed 1 as requested
  const [selectedLoadType, setSelectedLoadType] =
    useState<LoadType>("distributed1");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [clickedLabels, setClickedLabels] = useState<{
    dx: boolean;
    dy: boolean;
    dz: boolean;
  }>({
    dx: false,
    dy: false,
    dz: false,
  });
  const [loadValues, setLoadValues] = useState<LoadValues>({
    // Distributed 1 values
    dx: 0,
    dy: 0,
    dz: 0,
    // Distributed 2 values
    dxStart: 0,
    dxEnd: 0,
    dyStart: 0,
    dyEnd: 0,
    dzStart: 0,
    dzEnd: 0,
    // Concentrated values
    fx: 0,
    fy: 0,
    fz: 0,
    referenceNode: "N1",
    percentageFromNode: 0,
    // Twisted load values
    torque: 0,
    torqueDirection: "clockwise",
  });

  // String-backed input for concentrated distance to allow typing partial decimals like "1." or "1.2"
  const [distanceInput, setDistanceInput] = useState<string>(() =>
    String(loadValues.percentageFromNode ?? "")
  );

  // Keep distanceInput in sync when numeric percentageFromNode changes externally
  useEffect(() => {
    setDistanceInput(String(loadValues.percentageFromNode ?? ""));
  }, [loadValues.percentageFromNode, beamId]);

  // Drag state
  const [panelPosition, setPanelPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  const existingTrusses = getTrussesForBeam(beamId);

  // Beam length helper for concentrated distance input/validation
  const getBeamLength = (): number => {
    const beam = modelStore?.beams?.find((b) => b.id === beamId);
    const n1 = beam
      ? modelStore?.nodes?.find((n) => n.id === beam.startNodeId)
      : undefined;
    const n2 = beam
      ? modelStore?.nodes?.find((n) => n.id === beam.endNodeId)
      : undefined;
    if (n1 && n2) {
      const dx = n2.x - n1.x;
      const dy = n2.y - n1.y;
      const dz = n2.z - n1.z;
      return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    return 0;
  };

  // ADD: Effect to update node tags when beam or node data changes
  useEffect(() => {
    // This effect ensures tags update when beam connections change
    // or when node data is modified
    const updatedTags = getBeamNodeTags(beamId);
    const currentBeamIndex = getBeamIndex(beamId);

    console.log("🏷️ Node tags updated for beam:", beamId, {
      tags: updatedTags,
      beamIndex: currentBeamIndex,
      distributedSection:
        currentBeamIndex === 0 ? "Distributed 1" : "Distributed 2",
      selectedLoadType,
    });

    // Additional logging for debugging
    if (modelStore?.beams) {
      const currentBeam = modelStore.beams.find((b) => b.id === beamId);
      if (currentBeam) {
        // console.log('🔗 Beam connection details:', {
        //   beamId: currentBeam.id,
        //   startNodeId: currentBeam.startNodeId,
        //   endNodeId: currentBeam.endNodeId,
        //   startNode: modelStore.nodes?.find(n => n.id === currentBeam.startNodeId),
        //   endNode: modelStore.nodes?.find(n => n.id === currentBeam.endNodeId),
        //   beamIndex: currentBeamIndex
        // });
      }
    }
  }, [
    beamId,
    modelStore?.beams,
    modelStore?.nodes,
    modelStore?.selectedBeamIds,
    selectedLoadType,
  ]);

  // ADD: Initialize component with saved values for this beam when it first opens
  useEffect(() => {
    // Only apply fixes for distributed 1 and distributed 2
    if (
      selectedLoadType === "distributed1" ||
      selectedLoadType === "distributed2"
    ) {
      const savedInputs = beamInputData[beamId];
      if (savedInputs) {
        setLoadValues((prev) => ({
          ...prev,
          ...savedInputs,
        }));
      }
    }
  }, [beamId]); // Trigger when beamId changes (popup opens for different beam)

  // ADD: Load saved input values when load type changes
  useEffect(() => {
    // Only apply fixes for distributed 1 and distributed 2
    if (
      selectedLoadType === "distributed1" ||
      selectedLoadType === "distributed2"
    ) {
      const savedInputs = beamInputData[beamId];
      if (savedInputs) {
        setLoadValues((prev) => ({
          ...prev,
          ...savedInputs,
        }));
      }
    }
  }, [selectedLoadType, beamInputData]);

  // Update panel position when prop changes
  useEffect(() => {
    setPanelPosition(position);
  }, [position]);

  // Handle drag functionality
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && panelRef.current) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        const maxX = window.innerWidth - panelRef.current.offsetWidth;
        const maxY = window.innerHeight - panelRef.current.offsetHeight;

        setPanelPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleInputChange = (
    field: keyof LoadValues,
    value: number | string
  ) => {
    setLoadValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    // ADD: Persistence logic ONLY for distributed loads
    if (
      selectedLoadType === "distributed1" ||
      selectedLoadType === "distributed2"
    ) {
      const updatedData = {
        ...beamInputData,
        [beamId]: {
          ...beamInputData[beamId],
          [field]: value,
        },
      };
      setBeamInputData(updatedData);
      // Also update module storage for persistence across component remounts
      beamInputStorage[beamId] = {
        ...beamInputStorage[beamId],
        [field]: value,
      };

      // ADD: Zero-value removal logic ONLY for distributed loads
      if ((value === "0" || value === 0) && typeof field === "string") {
        const axis = field as string;
        // Only remove constraints for dx, dy, dz fields (not for start/end variations)
        if (
          ["dx", "dy", "dz"].includes(axis) ||
          ["dxStart", "dyStart", "dzStart", "dxEnd", "dyEnd", "dzEnd"].includes(
            axis
          )
        ) {
          removeSpecificConstraint(beamId, axis, selectedLoadType);
        }
      }
    }
    // Keep existing logic unchanged for concentrated loads
  };

  const handleLoadTypeChange = (loadType: LoadType) => {
    setSelectedLoadType(loadType);
    // Keep values when switching (as requested)
    // console.log(`Switched to ${loadType} panel`);
  };

  const handleApplyLoad = () => {
    let loadTypeName = "";

    // If multiple beamIds passed, apply load to all of them
    const targetIds = beamIds && beamIds.length > 0 ? beamIds : [beamId];

    targetIds.forEach((id) => {
      // Get fresh truss data for this beam
      const currentTrusses = getTrussesForBeam(id);

      switch (selectedLoadType) {
        case "distributed1":
          loadTypeName = "Distributed Uniform Load";

          const existingUniformTruss = currentTrusses.find(
            (truss) => truss.type === "distributed_uniform"
          );

          if (existingUniformTruss) {
            const { updateBeamTruss } = useBeamTrussStore.getState();
            updateBeamTruss(existingUniformTruss.id, {
              dx: loadValues.dx,
              dy: loadValues.dy,
              dz: loadValues.dz,
            });
          } else {
            addBeamTruss(
              id,
              "distributed_uniform",
              0,
              loadValues.dx,
              loadValues.dy,
              loadValues.dz
            );
          }
          break;

        case "distributed2":
          loadTypeName = "Distributed Variable Load";

          const existingVariableTruss = currentTrusses.find(
            (truss) => truss.type === "distributed_variable"
          );

          if (existingVariableTruss) {
            const { updateBeamTruss } = useBeamTrussStore.getState();
            updateBeamTruss(existingVariableTruss.id, {
              dxLeft: loadValues.dxStart,
              dyLeft: loadValues.dyStart,
              dzLeft: loadValues.dzStart,
              dxRight: loadValues.dxEnd,
              dyRight: loadValues.dyEnd,
              dzRight: loadValues.dzEnd,
            });
          } else {
            addBeamTruss(
              id,
              "distributed_variable",
              0,
              loadValues.dxStart,
              loadValues.dyStart,
              loadValues.dzStart,
              loadValues.dxEnd,
              loadValues.dyEnd,
              loadValues.dzEnd
            );
          }
          break;

        case "concentrated":
          loadTypeName = "Concentrated Load";

          const refNode =
            loadValues.referenceNode === "N1"
              ? "node1"
              : loadValues.referenceNode === "N2"
                ? "node2"
                : "node1";

          let percent = 0;
          const length = getBeamLength();
          if (loadValues.referenceNode === "Center") {
            percent = 50;
          } else {
            const distance = Math.max(
              0,
              Math.min(loadValues.percentageFromNode, length)
            );
            percent = length > 0 ? (distance / length) * 100 : 0;
          }

          addBeamTruss(
            id,
            "concentrated_load",
            0,
            loadValues.fx,
            loadValues.fy,
            loadValues.fz,
            0,
            0,
            0,
            loadValues.fx,
            loadValues.fy,
            loadValues.fz,
            refNode as "node1" | "node2",
            percent
          );
          break;

        case "twisted":
          loadTypeName = "Twisted Load";

          // Check if there's already a twisted load for this beam
          const existingTwistedLoad = currentTrusses.find(
            (truss) => truss.type === "twisted_load"
          );

          if (existingTwistedLoad) {
            // Update existing twisted load
            const { updateBeamTruss } = useBeamTrussStore.getState();
            updateBeamTruss(existingTwistedLoad.id, {
              torque: loadValues.torque,
              torqueDirection: loadValues.torqueDirection,
            });
          } else {
            // Create new twisted load
            addBeamTruss(
              id, // beamId
              "twisted_load", // type
              0, // pressureValue (not used)
              0, // dx (not used)
              0, // dy (not used)
              0, // dz (not used)
              0, // dxRight (not used)
              0, // dyRight (not used)
              0, // dzRight (not used)
              0, // fx (not used)
              0, // fy (not used)
              0, // fz (not used)
              undefined, // referenceNode (not used)
              undefined, // percentageFromNode (not used)
              undefined, // type4PeakLoad (not used)
              undefined, // type4Direction (not used)
              undefined, // type4PeakPosition (not used)
              undefined, // type4CurveType (not used)
              loadValues.torque, // torque value
              loadValues.torqueDirection // torque direction
            );
          }
          break;
      }
    });

    setSuccessMessage(
      `✅ ${loadTypeName} applied successfully to ${targetIds.length} beam(s)`
    );
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleApplyAndClose = () => {
    // Apply the load first
    handleApplyLoad();
    // Close the popup immediately
    onClose();
  };

  const handleLabelClick = (label: "dx" | "dy" | "dz") => {
    setClickedLabels((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // ADD: Constraint removal function (distributed loads only)
  const removeSpecificConstraint = (
    beamId: string,
    axis: string,
    loadType: LoadType
  ) => {
    // Only apply to distributed loads
    if (loadType === "distributed1" || loadType === "distributed2") {
      // Get existing trusses for this beam
      const existingTrusses = getTrussesForBeam(beamId);

      // Find and remove trusses that match the zero-valued constraint
      existingTrusses.forEach((truss) => {
        let shouldRemove = false;

        if (loadType === "distributed1") {
          // For distributed1, check if the specific axis is zero
          if (axis === "dx" && truss.dx === 0) shouldRemove = true;
          if (axis === "dy" && truss.dy === 0) shouldRemove = true;
          if (axis === "dz" && truss.dz === 0) shouldRemove = true;
        } else if (loadType === "distributed2") {
          // For distributed2, check if both start and end values are zero
          if (axis.includes("dx") && truss.dxLeft === 0 && truss.dxRight === 0)
            shouldRemove = true;
          if (axis.includes("dy") && truss.dyLeft === 0 && truss.dyRight === 0)
            shouldRemove = true;
          if (axis.includes("dz") && truss.dzLeft === 0 && truss.dzRight === 0)
            shouldRemove = true;
        }

        if (shouldRemove) {
          removeBeamTruss(truss.id);
        }
      });
    }
    // Do nothing for concentrated loads - keep existing behavior
  };

  // Render the toggle buttons at the top
  const renderToggleButtons = () => (
    <div className="flex space-x-1 mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 width-full justify-content-between">
      <button
        onClick={() => handleLoadTypeChange("distributed1")}
        className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
          selectedLoadType === "distributed1"
            ? "bg-blue-500 text-white shadow-sm"
            : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
        }`}
      >
        Distributed 1
      </button>
      <button
        onClick={() => handleLoadTypeChange("distributed2")}
        className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
          selectedLoadType === "distributed2"
            ? "bg-blue-500 text-white shadow-sm"
            : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
        }`}
      >
        Distributed 2
      </button>
      <button
        onClick={() => handleLoadTypeChange("concentrated")}
        className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
          selectedLoadType === "concentrated"
            ? "bg-blue-500 text-white shadow-sm"
            : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
        }`}
      >
        Concentrated
      </button>
      <button
        onClick={() => handleLoadTypeChange("twisted")}
        className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
          selectedLoadType === "twisted"
            ? "bg-purple-500 text-white shadow-sm"
            : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
        }`}
      >
        Twisted Load
      </button>
    </div>
  );

  // Render Distributed 1 Panel (DX, DY, DZ with single input boxes)
  const renderDistributed1Panel = () => (
    <div className="space-y-4">
      {/* Node tags removed for Distributed 1 as requested */}

      <div className="space-y-3">
        {/* DX Row */}
        <div
          className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800 cursor-pointer"
          onClick={() => handleLabelClick("dx")}
        >
          <label
            className={`text-sm font-medium ${clickedLabels.dx ? "text-blue-400" : "text-blue-400"}`}
          >
            DX
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="^-?\\d*(?:[.,]\\d*)?$"
            value={loadValues.dx}
            onChange={(e) =>
              handleInputChange("dx", parseFloat(e.target.value) || 0)
            }
            onClick={(e) => e.stopPropagation()}
            className="w-20 px-2 py-1 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
        </div>

        {/* DY Row */}
        <div
          className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800 cursor-pointer"
          onClick={() => handleLabelClick("dy")}
        >
          <label
            className={`text-sm font-medium ${clickedLabels.dy ? "text-green-400" : "text-blue-400"}`}
          >
            DY
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="^-?\\d*(?:[.,]\\d*)?$"
            value={loadValues.dy}
            onChange={(e) =>
              handleInputChange("dy", parseFloat(e.target.value) || 0)
            }
            onClick={(e) => e.stopPropagation()}
            className="w-20 px-2 py-1 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
        </div>

        {/* DZ Row */}
        <div
          className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800 cursor-pointer"
          onClick={() => handleLabelClick("dz")}
        >
          <label
            className={`text-sm font-medium ${clickedLabels.dz ? "text-red-400" : "text-blue-400"}`}
          >
            DZ
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="^-?\\d*(?:[.,]\\d*)?$"
            value={loadValues.dz}
            onChange={(e) =>
              handleInputChange("dz", parseFloat(e.target.value) || 0)
            }
            onClick={(e) => e.stopPropagation()}
            className="w-20 px-2 py-1 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
        </div>
      </div>
    </div>
  );

  // Render Distributed 2 Panel (DX, DY, DZ with two input boxes each) with inline node tags
  const renderDistributed2Panel = () => (
    <div className="space-y-4">
      <div className="space-y-3">
        {/* Header row: show start/end node ids once (Snode id - Enode id - box) */}
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-8" />
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">Snode id -</span>
              <div className="w-12 h-6 border rounded flex items-center justify-center text-xs font-medium dark:text-gray-200 bg-transparent">
                {beamNodeTags.n1}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">Enode id -</span>
              <div className="w-12 h-6 border rounded flex items-center justify-center text-xs font-medium dark:text-gray-200 bg-transparent">
                {beamNodeTags.n2}
              </div>
            </div>
          </div>
        </div>

        {/* DX Row - Two inputs */}
        <div className="flex items-start space-x-3">
          <label className="w-8 pt-5 text-sm font-medium text-red-600">
            DX
          </label>
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                inputMode="decimal"
                pattern="^-?\\d*(?:[.,]\\d*)?$"
                value={loadValues.dxStart}
                onChange={(e) =>
                  handleInputChange("dxStart", parseFloat(e.target.value) || 0)
                }
                className="w-full px-2 py-1 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Start"
              />
            </div>
            <div>
              <input
                type="text"
                inputMode="decimal"
                pattern="^-?\\d*(?:[.,]\\d*)?$"
                value={loadValues.dxEnd}
                onChange={(e) =>
                  handleInputChange("dxEnd", parseFloat(e.target.value) || 0)
                }
                className="w-full px-2 py-1 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="End"
              />
            </div>
          </div>
        </div>

        {/* DY Row - Two inputs */}
        <div className="flex items-start space-x-3">
          <label className="w-8 pt-5 text-sm font-medium text-green-600">
            DY
          </label>
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                inputMode="decimal"
                pattern="^-?\\d*(?:[.,]\\d*)?$"
                value={loadValues.dyStart}
                onChange={(e) =>
                  handleInputChange("dyStart", parseFloat(e.target.value) || 0)
                }
                className="w-full px-2 py-1 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Start"
              />
            </div>
            <div>
              <input
                type="text"
                inputMode="decimal"
                pattern="^-?\\d*(?:[.,]\\d*)?$"
                value={loadValues.dyEnd}
                onChange={(e) =>
                  handleInputChange("dyEnd", parseFloat(e.target.value) || 0)
                }
                className="w-full px-2 py-1 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="End"
              />
            </div>
          </div>
        </div>

        {/* DZ Row - Two inputs */}
        <div className="flex items-start space-x-3">
          <label className="w-8 pt-5 text-sm font-medium text-blue-600">
            DZ
          </label>
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                inputMode="decimal"
                pattern="^-?\\d*(?:[.,]\\d*)?$"
                value={loadValues.dzStart}
                onChange={(e) =>
                  handleInputChange("dzStart", parseFloat(e.target.value) || 0)
                }
                className="w-full px-2 py-1 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Start"
              />
            </div>
            <div>
              <input
                type="text"
                inputMode="decimal"
                pattern="^-?\\d*(?:[.,]\\d*)?$"
                value={loadValues.dzEnd}
                onChange={(e) =>
                  handleInputChange("dzEnd", parseFloat(e.target.value) || 0)
                }
                className="w-full px-2 py-1 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="End"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Concentrated Panel (Radio buttons + percentage + DX, DY, DZ horizontal)
  const renderConcentratedPanel = () => (
    <div className="space-y-4">
      {/* <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Concentrated Load
      </h4> */}

      {/* Top Section: Radio buttons and distance with beam length */}
      <div className="space-y-3">
        {/* Radio buttons */}
        <div className="flex space-x-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="referenceNode"
              value="N1"
              checked={loadValues.referenceNode === "N1"}
              onChange={(e) =>
                handleInputChange(
                  "referenceNode",
                  e.target.value as ReferenceNode
                )
              }
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {beamNodeTags.n1}
            </span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="referenceNode"
              value="N2"
              checked={loadValues.referenceNode === "N2"}
              onChange={(e) =>
                handleInputChange(
                  "referenceNode",
                  e.target.value as ReferenceNode
                )
              }
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {beamNodeTags.n2}
            </span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="referenceNode"
              value="Center"
              checked={loadValues.referenceNode === "Center"}
              onChange={(e) =>
                handleInputChange(
                  "referenceNode",
                  e.target.value as ReferenceNode
                )
              }
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Center
            </span>
          </label>
        </div>

        {/* Beam length info */}
        <div className="text-xs text-gray-600 dark:text-gray-300">
          Beam length:{" "}
          <span className="font-medium">{getBeamLength().toFixed(2)}</span>{" "}
          units
        </div>

        {/* Distance input box (0 .. beam length) - Hidden when Center is selected; center implies 50% */}
        {loadValues.referenceNode !== "Center" && (
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              pattern="^\\d{0,}?(?:\\.\\d{0,2})?$"
              value={distanceInput}
              onChange={(e) => {
                const rawS = (e.target.value || "").toString();
                // Allow typing; validate format locally
                const clean = rawS.replace(/,/g, ".");
                if (!/^\d*(?:\.\d{0,2})?$/.test(clean) && clean !== "") {
                  // invalid pattern (more than 2 decimals or invalid chars), ignore
                  return;
                }
                setDistanceInput(clean);
              }}
              onBlur={() => {
                const length = getBeamLength();
                const val = parseFloat(distanceInput);
                if (isNaN(val)) {
                  handleInputChange("percentageFromNode", 0);
                  return;
                }
                if (val < 0) {
                  setWarningMessage("Distance cannot be negative");
                  setTimeout(() => setWarningMessage(""), 2500);
                  return;
                }
                if (val > length) {
                  setWarningMessage(
                    `Distance cannot exceed beam length (${length.toFixed(2)})`
                  );
                  setTimeout(() => setWarningMessage(""), 3000);
                  return; // reject update
                }
                // Valid numeric value in range; persist into numeric state
                handleInputChange("percentageFromNode", val);
              }}
              className="w-full px-3 py-2 pr-16 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder={`Distance from ${loadValues.referenceNode} (0..${getBeamLength().toFixed(2)})`}
              title={`Beam length: ${getBeamLength().toFixed(2)} units`}
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xs pointer-events-none">
              units
            </span>
            {warningMessage && (
              <div
                className="mt-2 text-xs text-red-600 dark:text-red-400"
                role="alert"
                aria-live="polite"
              >
                {warningMessage}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Section: DX, DY, DZ horizontally aligned */}
      <div className="space-y-3">
        {/* DX - input below label */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-red-600 mb-1">
            DX
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="^-?\\d*(?:[.,]\\d*)?$"
            value={loadValues.fx}
            onChange={(e) =>
              handleInputChange("fx", parseFloat(e.target.value) || 0)
            }
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="FX value"
          />
        </div>

        {/* DY and DZ side by side */}
        <div className="flex space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-green-600 mb-1">
              DY
            </label>
            <input
              type="text"
              inputMode="decimal"
              pattern="^-?\\d*(?:[.,]\\d*)?$"
              value={loadValues.fy}
              onChange={(e) =>
                handleInputChange("fy", parseFloat(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="FY value"
            />
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-blue-600 mb-1">
              DZ
            </label>
            <input
              type="text"
              inputMode="decimal"
              pattern="^-?\\d*(?:[.,]\\d*)?$"
              value={loadValues.fz}
              onChange={(e) =>
                handleInputChange("fz", parseFloat(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="FZ value"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render Twisted Load Panel (Torsional Moment)
  const renderTwistedLoadPanel = () => (
    <div className="space-y-4">
      {/* Torque Value Input */}
      <div>
        <label className="block text-sm font-medium text-purple-600 mb-2">
          Torque Value (N⋅m)
        </label>
        <input
          type="number"
          value={loadValues.torque}
          onChange={(e) =>
            handleInputChange("torque", parseFloat(e.target.value) || 0)
          }
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="Enter torque value"
          step="0.1"
        />
      </div>

      {/* Direction Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Direction
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="torqueDirection"
              value="clockwise"
              checked={loadValues.torqueDirection === "clockwise"}
              onChange={(e) =>
                handleInputChange("torqueDirection", e.target.value)
              }
              className="w-4 h-4 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-900 dark:text-gray-300">
              Clockwise
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="torqueDirection"
              value="anticlockwise"
              checked={loadValues.torqueDirection === "anticlockwise"}
              onChange={(e) =>
                handleInputChange("torqueDirection", e.target.value)
              }
              className="w-4 h-4 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-900 dark:text-gray-300">
              Anti-clockwise
            </span>
          </label>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <p className="text-xs text-purple-700">
          <strong>Visual:</strong> A 75% circular arrow will appear at the beam
          midpoint, wrapping around the beam in the selected direction.
        </p>
      </div>
    </div>
  );

  return (
    <div
      ref={panelRef}
      className={
        inlineMode
          ? "w-full bg-gray-900/30 dark:bg-gray-900/30 rounded-md border border-gray-700 p-3 overflow-y-auto" // inline compact style
          : `fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-96 z-50 border-2 border-gray-200 dark:border-gray-600 ${
              isDragging ? "cursor-grabbing shadow-2xl" : "cursor-auto"
            }`
      }
      style={
        inlineMode
          ? { maxHeight: "80%", overflowY: "auto" } // 👈 scroll only in inline mode
          : {
              left: panelPosition.x + "px",
              top: panelPosition.y + "px",
              maxHeight: "80vh",
              overflowY: "auto",
            }
      }
    >
      {/* Header only for popup */}
      {!inlineMode && (
        <div
          className={`flex justify-between items-center p-4 pb-2 border-b border-gray-200 dark:border-gray-600 ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          onMouseDown={handleMouseDown}
        >
          <h3 className="text-lg font-semibold dark:text-white select-none">
            Beam Load
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl font-bold cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mx-4 mt-2 p-3 bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-md">
          <p className="text-sm text-green-800 dark:text-green-200 text-center font-medium">
            {successMessage}
          </p>
        </div>
      )}

      {/* Content Area */}
      <div className="p-4">
        <div className="space-y-4">
          {/* Toggle Buttons */}
          {renderToggleButtons()}

          {/* Dynamic Panel Content */}
          {selectedLoadType === "distributed1" && renderDistributed1Panel()}
          {selectedLoadType === "distributed2" && renderDistributed2Panel()}
          {selectedLoadType === "concentrated" && renderConcentratedPanel()}
          {selectedLoadType === "twisted" && renderTwistedLoadPanel()}

          {/* Apply and Close Buttons */}
          <div className="flex gap-2 pt-4 border-t dark:border-gray-600">
            <button
              onClick={() => {
                if (selectedLoadType === "concentrated" && warningMessage)
                  return;
                handleApplyLoad();
              }}
              className="px-7 py-2 text-sm text-white rounded-md transition-colors bg-blue-500 hover:bg-blue-600"
            >
              Apply
            </button>
            <button
              onClick={() => {
                if (selectedLoadType === "concentrated" && warningMessage)
                  return;
                handleApplyAndClose();
              }}
              className="flex-1 px-2 py-2 text-sm text-white rounded-md transition-colors bg-green-500 hover:bg-green-600"
            >
              Apply & Close
            </button>
            <button
              onClick={onClose}
              className="px-7 py-3 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>

          {/* Existing Loads Display - ONLY for concentrated loads */}
          {selectedLoadType === "concentrated" &&
            existingTrusses.filter(
              (truss) => truss.type === "concentrated_load"
            ).length > 0 && (
              <div className="space-y-3 pt-4 border-t dark:border-gray-600">
                <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                  Existing Concentrated Loads:
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {existingTrusses
                    .filter((truss) => truss.type === "concentrated_load") // Only show concentrated loads
                    .map((truss) => (
                      <div
                        key={truss.id}
                        className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded"
                      >
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: truss.color }}
                          />
                          <span className="text-xs dark:text-gray-300">
                            {truss.label} ({truss.type})
                          </span>
                        </div>
                        <button
                          onClick={() => removeBeamTruss(truss.id)}
                          className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded"
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
